import type { CatHandle, CatPose } from "./cat";

// Cat debug panel — toggle button, pose row, per-pose behavior buttons,
// speed slider, transition progress bar. Lives next to the rest of the
// HUD plumbing in hud.ts since the same toggle gates the physics-debug
// overlay (subscribed externally via onDebugToggle).

const POSES: readonly CatPose[] = ["stand", "sit", "lie", "sleep"];

export interface CatDebugHud {
  onDebugToggle: (cb: (enabled: boolean) => void) => void;
  setCat: (cat: CatHandle) => void;
}

export function attachCatDebugHud(): CatDebugHud {
  const debugToggle = document.getElementById(
    "debug-toggle",
  ) as HTMLButtonElement;
  const catDebugPanel = document.getElementById(
    "cat-debug-panel",
  ) as HTMLElement;
  const catDebugBody = document.getElementById(
    "cat-debug-buttons",
  ) as HTMLElement;
  const catSpeedInput = document.getElementById(
    "cat-debug-speed",
  ) as HTMLInputElement;
  const catSpeedValue = document.getElementById(
    "cat-debug-speed-value",
  ) as HTMLElement;
  const catProgressRow = document.getElementById(
    "cat-debug-progress-row",
  ) as HTMLElement;
  const catProgressLabel = document.getElementById(
    "cat-debug-progress-label",
  ) as HTMLElement;
  const catProgressBar = document.getElementById(
    "cat-debug-progress",
  ) as HTMLProgressElement;
  const catProgressPct = document.getElementById(
    "cat-debug-progress-pct",
  ) as HTMLElement;

  let debugEnabled = false;
  const debugSubscribers: Array<(enabled: boolean) => void> = [];
  // Cached cat handle so the toggle and slider can reach the cat
  // without re-wiring on each cat-load. Set by setCat below.
  let catHandle: CatHandle | null = null;

  debugToggle.addEventListener("click", () => {
    debugEnabled = !debugEnabled;
    debugToggle.setAttribute("aria-pressed", String(debugEnabled));
    for (const cb of debugSubscribers) cb(debugEnabled);
    catDebugPanel.dataset.open = String(debugEnabled);
    catDebugPanel.setAttribute("aria-hidden", String(!debugEnabled));
    // Pause autonomous AI while debug is on so manual pose triggers
    // aren't immediately overridden by the next AI pick.
    if (catHandle) catHandle.debug.setAIEnabled(!debugEnabled);
  });

  // Speed slider: 0 = freeze, 1 = real-time, 2 = 2× fast-forward.
  // Affects only the cat (via debug.setAnimSpeed); physics + scene
  // continue at real time so the cat slows down against a live world.
  catSpeedInput.addEventListener("input", () => {
    const v = Number(catSpeedInput.value);
    catSpeedValue.textContent = `${v.toFixed(2)}×`;
    if (catHandle) catHandle.debug.setAnimSpeed(v);
  });

  return {
    onDebugToggle: (cb) => debugSubscribers.push(cb),
    setCat: (cat) => {
      catHandle = cat;
      // Apply current debug + slider state on connection so a cat that
      // loads after the user has already toggled debug / moved the
      // slider doesn't ignore those choices.
      cat.debug.setAIEnabled(!debugEnabled);
      cat.debug.setAnimSpeed(Number(catSpeedInput.value));
      const groups = cat.behaviorsByPose();
      catDebugBody.replaceChildren();

      // Pose row — clicking transitions the cat into that pose. The
      // active pose has a different visual style; we update it on poll.
      const poseRow = document.createElement("div");
      poseRow.className = "cat-debug-pose-row";
      const poseButtons = new Map<CatPose, HTMLButtonElement>();
      for (const pose of POSES) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "cat-debug-pose-btn";
        btn.textContent = pose;
        btn.addEventListener("click", () => cat.transitionToPose(pose));
        poseButtons.set(pose, btn);
        poseRow.appendChild(btn);
      }
      catDebugBody.appendChild(poseRow);

      // Per-pose behavior sections — each header lists which clips are
      // legal in that pose. Clicking a clip from a non-active pose is
      // safe; cat.triggerBehavior auto-routes via a pose transition.
      for (const pose of POSES) {
        const clips = groups[pose] ?? [];
        if (clips.length === 0) continue;
        const section = document.createElement("div");
        section.className = "cat-debug-section";
        section.dataset.pose = pose;

        const header = document.createElement("div");
        header.className = "cat-debug-section-header";
        header.textContent = pose;
        section.appendChild(header);

        const buttons = document.createElement("div");
        buttons.className = "cat-debug-buttons";
        for (const clip of clips) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "cat-debug-btn";
          btn.textContent = clip;
          btn.addEventListener("click", () => cat.triggerBehavior(clip));
          buttons.appendChild(btn);
        }
        section.appendChild(buttons);
        catDebugBody.appendChild(section);
      }

      // Poll the cat's pose four times a second so the active highlight
      // tracks pose transitions as they finish, and so we can grey out
      // the buttons while the cat is mid-transition / mid-turn —
      // clicking another trigger then would cancelInflight() and start
      // a fresh transition, which is rarely what the user wants while
      // watching one play.
      const refreshActive = (): void => {
        const cur = cat.currentPose();
        const busy = cat.debug.isBusy();
        for (const [pose, btn] of poseButtons) {
          btn.dataset.active = String(pose === cur);
          btn.disabled = busy;
        }
        for (const btn of catDebugBody.querySelectorAll(".cat-debug-btn")) {
          (btn as HTMLButtonElement).disabled = busy;
        }
        for (const sec of catDebugBody.querySelectorAll(".cat-debug-section")) {
          (sec as HTMLElement).dataset.active = String(
            (sec as HTMLElement).dataset.pose === cur,
          );
        }
        // Transition progress bar — shows the currently-playing
        // pose-transition or turn clip's progress; reads `Idle` and
        // greys out when neither is in flight.
        const progress = cat.debug.transitionProgress();
        if (progress === null) {
          catProgressRow.dataset.active = "false";
          catProgressLabel.textContent = "Idle";
          catProgressBar.value = 0;
          catProgressPct.textContent = "—";
        } else {
          catProgressRow.dataset.active = "true";
          catProgressLabel.textContent = cat.currentClip();
          catProgressBar.value = progress;
          catProgressPct.textContent = `${Math.round(progress * 100)}%`;
        }
      };
      refreshActive();
      // 60 ms ≈ 16 Hz: snappy enough that a quick transition (≤ 1 s)
      // still re-enables buttons within a few frames of finishing,
      // without burning cycles on DOM walks.
      window.setInterval(refreshActive, 60);
    },
  };
}
